<script setup lang="ts">
import { ref } from "vue";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { useGetUser } from "~/modules/user/composables/useGetUser";
import {
  PATH_USER_ACCOUNT_ADMINISTRATION_LANGUAGE,
  PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
  PATH_USER_ACCOUNT_LANGUAGE,
  PATH_USER_ACCOUNT_PROFILE,
} from "~/modules/user/constants";
import type { UserLanguage } from "~/modules/user/models/UserLanguage.interface";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import { useUserSession } from "~/modules/user/stores/useUserSession";

const { userSession } = useUserSession();

const businessUnitCode = ref();
const organizationCode = ref();

switch (userSession?.entity.type) {
  case UserSessionEntityType.BUSINESS_UNIT:
    businessUnitCode.value = userSession.entity.code;
    break;
  case UserSessionEntityType.OPERATOR:
  case UserSessionEntityType.ADMINISTRATION:
    organizationCode.value = userSession.entity.code;
    break;
}

const {
  result: user,
  isLoading,
  error,
  getUser,
} = useGetUser({
  organizationCode: organizationCode.value,
  businessUnitCode: businessUnitCode.value,
});

const userAccountProfile = ref();

function saveLanguage(language: UserLanguage) {
  userAccountProfile.value.saveLanguage(language);
}

//
// Navigation section. Logic / configuration responsible for the navigation.
//
const profileTabId = "profile";
const languageTabId = "language";

const tabNavigations: TabNavigation[] = [
  {
    id: profileTabId,
    redirectPath:
      userSession?.entity.type === UserSessionEntityType.OPERATOR
        ? PATH_USER_ACCOUNT_PROFILE
        : PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
    labelTranslationKey: "user.userAccount.userInformations.title",
  },
  {
    id: languageTabId,
    redirectPath:
      userSession?.entity.type === UserSessionEntityType.OPERATOR
        ? `${PATH_USER_ACCOUNT_LANGUAGE}`
        : `${PATH_USER_ACCOUNT_ADMINISTRATION_LANGUAGE}`,
    labelTranslationKey: "user.userAccount.language.title",
  },
];

//
// Lifecycle Hooks. See: https://vuejs.org/api/composition-api-lifecycle
//
onMounted(async () => {
  const login = userSession?.login;
  if (login) {
    await getUser(login);
  }
});
</script>

<template>
  <page-component :is-loading="isLoading" :error="error">
    <tabs-with-navigation :tab-navigations="tabNavigations">
      <template #[profileTabId]>
        <user-account-information
          ref="userAccountProfile"
          v-model:user="user"
          :use-external-idp="userSession?.entity.useExternalIdp"
        />
      </template>
      <template #[languageTabId]>
        <user-account-language
          v-model:user="user"
          @save-language="saveLanguage"
        />
      </template>
    </tabs-with-navigation>
  </page-component>
</template>
